import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, Search, Globe, Shield } from "lucide-react";

function NavItem({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace(/^\//, "") || "home"}`}
      className={({ isActive }) =>
        `text-[13px] tracking-[0.05em] font-medium transition-colors link-underline ${
          isActive ? "text-ink" : "text-slate600 hover:text-ink"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export default function Header() {
  const { t, lang, setLang, languages } = useLang();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const loc = useLocation();

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/war-crimes", label: t.nav.war_crimes },
    { to: "/terror-organizations", label: t.nav.terror_crimes },
    { to: "/monitoring", label: t.nav.monitoring },
    { to: "/studies", label: t.nav.studies },
    { to: "/analysis", label: t.nav.analysis },
    { to: "/news", label: t.nav.news },
    { to: "/director", label: t.nav.director_message },
    { to: "/media", label: t.nav.media },
    { to: "/publications", label: t.nav.publications },
    { to: "/contact", label: t.nav.contact },
  ];

  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(q.trim())}`;
    }
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate200"
      data-testid="site-header"
    >
      {/* Top Strip - language switcher + admin */}
      <div className="border-b border-slate200/60 bg-slate50">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between h-9 text-[11px]">
          <div className="flex items-center gap-3 text-slate600">
            <span className="label-eyebrow text-[10px]">MESC-CE</span>
            <span className="hidden md:inline text-slate500">{t.home.eyebrow}</span>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-1 border border-slate200 bg-white"
              data-testid="language-switcher"
            >
              <Globe className="w-3 h-3 ms-2 text-slate500" />
              {languages.map((l) => (
                <button
                  key={l.code}
                  data-testid={`language-switcher-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-2 h-7 text-[11px] font-medium transition-colors ${
                    lang === l.code
                      ? "bg-ink text-white"
                      : "text-slate600 hover:text-ink"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            {/* Admin link is only rendered for already-authenticated admins.
                Public visitors see no path to the admin area in the UI;
                login is reachable only by typing the URL directly. */}
            {user && (
              <Link
                to="/admin"
                data-testid="admin-link"
                className="flex items-center gap-1 text-[11px] text-slate600 hover:text-ink link-underline"
              >
                <Shield className="w-3 h-3" /> {t.nav.admin}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Brand row */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-9 h-9 bg-slate500 text-white grid place-items-center text-xs font-bold tracking-widest" aria-hidden>
            M
          </div>
          <div className="leading-tight">
            <div className="heading-serif text-[19px] sm:text-[22px] text-ink font-semibold">
              {t.site_name}
            </div>
            <div className="label-eyebrow text-[10px] mt-0.5">
              Security · Counter-Extremism · Strategic Research
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <button
            data-testid="open-search-btn"
            onClick={() => setSearchOpen((s) => !s)}
            className="hidden md:flex items-center gap-2 border border-slate200 px-3 h-9 text-[12px] text-slate600 hover:border-ink hover:text-ink transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            {t.filters.search_placeholder.split("…")[0]}…
          </button>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-ink"
            onClick={() => setMobileOpen((s) => !s)}
            aria-label="menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Search expanded */}
      {searchOpen && (
        <div className="border-t border-slate200 bg-white">
          <form
            onSubmit={onSubmitSearch}
            className="max-w-[1400px] mx-auto px-5 lg:px-10 py-3 flex items-center gap-3"
          >
            <Search className="w-4 h-4 text-slate500" />
            <input
              autoFocus
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.filters.search_placeholder}
              className="flex-1 bg-transparent outline-none text-[14px] text-ink"
            />
            <button
              data-testid="header-search-submit"
              className="px-3 h-8 bg-ink text-white text-[12px] font-medium"
              type="submit"
            >
              {t.common.ai_smart_search}
            </button>
          </form>
        </div>
      )}

      {/* Desktop nav */}
      <nav className="hidden lg:block border-t border-slate200 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center gap-6 h-11 overflow-x-auto">
          {navItems.map((n) => (
            <NavItem key={n.to} to={n.to} label={n.label} />
          ))}
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <nav
          data-testid="mobile-nav"
          className="lg:hidden border-t border-slate200 bg-white"
          key={loc.pathname}
        >
          <div className="px-5 py-4 grid gap-3">
            {navItems.map((n) => (
              <NavItem
                key={n.to}
                to={n.to}
                label={n.label}
                onClick={() => setMobileOpen(false)}
              />
            ))}
            <form onSubmit={onSubmitSearch} className="flex items-center gap-2 border border-slate200 px-3 h-10 mt-2">
              <Search className="w-4 h-4 text-slate500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.filters.search_placeholder}
                className="flex-1 bg-transparent outline-none text-[13px]"
              />
            </form>
          </div>
        </nav>
      )}
    </header>
  );
}
